/**
 * My3DTasksTab
 * Shows 3D designer's assigned tasks with status tracking
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { drawing3DAPI } from '../../../services/api';
import { toast } from 'react-toastify';

export default function My3DTasksTab() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTasks();
  }, []);

  async function fetchTasks() {
    setLoading(true);
    try {
      const response = await drawing3DAPI.getMyTasks();
      setTasks(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching 3D tasks:', err);
      setError(err.message || 'Failed to load tasks');
      toast.error('Failed to load 3D tasks');
    } finally {
      setLoading(false);
    }
  }

  function getStatusBadge(status) {
    const badges = {
      assigned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      submitted: 'bg-green-100 text-green-800',
      amendment_required: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800'
    };
    const labels = {
      assigned: 'Assigned',
      in_progress: 'In Progress',
      submitted: 'Submitted',
      amendment_required: 'Amendment Required',
      approved: 'Approved'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  }

  function formatDateTime(dateString) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No 3D tasks assigned</h3>
        <p className="mt-1 text-sm text-gray-500">
          You have no assigned 3D visualization tasks. Tasks will appear here once assigned by SID/ASID.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Project
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Client
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '180px' }}>
              Location
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Assigned
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Assigned By
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {tasks.map((task) => (
            <tr
              key={task.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => navigate(`/projects/${task.designProjectId}`)}
            >
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                  {task.projectNumber}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm text-gray-900">{task.clientName || 'N/A'}</div>
              </td>
              <td className="px-4 py-3" style={{ maxWidth: '200px' }}>
                <div className="text-xs text-gray-700 leading-tight truncate" title={task.propertyAddress}>
                  {task.propertyAddress || 'N/A'}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {formatDateTime(task.assignedAt)}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm text-gray-900">{task.assignedBy || 'N/A'}</div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                {getStatusBadge(task.taskStatus)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
